import React from 'react';
import { z } from 'zod';
import { useForm, Form, FormItem, FormLabel, FormControl, FormMessage } from './Form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './Dialog';
import { Button } from './Button';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { useCreateSession } from '../helpers/useSessionsApi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const createSessionSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
});

interface CreateSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function generateDefaultProjectName() {
  const datePart = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  return `Project ${datePart}`;
}

export const CreateSessionDialog = ({ open, onOpenChange }: CreateSessionDialogProps) => {
  const createSession = useCreateSession();
  const navigate = useNavigate();
  const form = useForm({
    defaultValues: {
      name: generateDefaultProjectName(),
      description: '',
    },
    schema: createSessionSchema,
  });

  React.useEffect(() => {
    if (!open) return;
    form.setValues((prev) => ({
      ...prev,
      name: prev.name?.trim() ? prev.name : generateDefaultProjectName(),
    }));
  }, [open]);

  const onSubmit = (values: z.infer<typeof createSessionSchema>) => {
    const resolvedName = values.name?.trim() || generateDefaultProjectName();
    const payload = { ...values, name: resolvedName, template: 'none' };
    createSession.mutate(payload as any, {
      onSuccess: (data) => {
        try {
          const sid = data?.session?.id;
          if (sid) {
            try {
              localStorage.setItem(`session_template_${sid}`, 'none');
              if (values.description && values.description.trim()) {
                localStorage.setItem(`session_auto_prompt_${sid}`, values.description.trim());
              }
              localStorage.setItem(`session_name_${sid}`, resolvedName);
            } catch (e) {
              /* ignore */
            }
            navigate(`/session/${sid}`);
          }
        } catch (e) {
          void e;
        }

        toast.success('Session created successfully');
        onOpenChange(false);
        form.setValues({ name: generateDefaultProjectName(), description: '' });
      },
      onError: (error) => {
        toast.error('Failed to create session');
        console.error(error);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Workspace</DialogTitle>
          <DialogDescription>
            Start a new secure collaboration session. You can choose framework in chat after
            creation.
          </DialogDescription>
        </DialogHeader>

        {open && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormItem name="name">
                <FormLabel>Project Name (default generated)</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., Q4 Roadmap Planning"
                    value={form.values.name || ''}
                    onChange={(e) => form.setValues((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>

              <FormItem name="description">
                <FormLabel>Description (Optional)</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Briefly describe the goal of this session..."
                    value={form.values.description || ''}
                    onChange={(e) =>
                      form.setValues((prev) => ({ ...prev, description: e.target.value }))
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>

              <DialogFooter>
                <Button variant="secondary" type="button" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createSession.isPending}>
                  {createSession.isPending ? 'Creating...' : 'Create & Start Chat'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
